import { Alert, Col, Descriptions, Flex, Row } from "@/atoms"
import { NO_VALUE_PLACEHOLDER } from "@/const/default-values"
import { useTranslation } from "@/hooks/useTranslation"
import { InfoCircleOutlined } from "@/icons"
import { CommitHash } from "@/molecules/CommitHash/CommitHash"
import { Heading } from "@/molecules/Heading/Heading"
import { InformationAlert } from "@/molecules/InformationAlert"
import { ScanStatusTag } from "@/molecules/ScanStatusTag"
import { useBranchEntity } from "@/organisms/Drawers/applications/inventory/useInventory"
import { OverviewCard } from "@/organisms/Drawers/components/OverviewCard"
import { RepositoryTopics } from "@/organisms/Drawers/components/RepositoryTopics"
import { ResourceOverviewCard } from "@/organisms/Drawers/components/ResourceOverviewCard"
import { PackageManagersList } from "../PackageManagersList"
import { Users } from "./Users"

import styles from "./OverviewTabCodeRepositoryDrawer.module.scss"

import type { OverviewTabCodeRepositoryDrawerComponent } from "."

export const OverviewTabCodeRepositoryDrawer: OverviewTabCodeRepositoryDrawerComponent =
  (props) => {
    const [context, { isPackageManagerEstimated }] = useBranchEntity(
      props.record?.id
    )
    const { response, isLoading } = context
    const { data } = response || {}
    const { t } = useTranslation()
    const { t: tOverview } = useTranslation("translation", {
      keyPrefix: "inventory.codeRepositoriesTab.drawer.overview"
    })
    const record = data?.[0] || ({ scanInfo: {} } as any)
    const repositoryName = record?.repositoryName

    const infoMessage = isPackageManagerEstimated
      ? t("inventory.codeRepositoriesTab.drawer.estimatedPackageManagerMessage")
      : undefined

    return (
      <Flex vertical gap={16} className="body-tab-drawer-ks">
        {infoMessage && <InformationAlert message={infoMessage} />}
        {!!record.scanInfo.message && (
          <Alert
            showIcon
            icon={<InfoCircleOutlined />}
            type={record.scanInfo.status === "Incomplete" ? "warning" : "info"}
            title={record.scanInfo.message}
          />
        )}
        <Row gutter={16}>
          <Col span={14}>
            <OverviewCard
              isLoading={isLoading}
              title={tOverview("codeRepositoryDetails")}
              items={[
                { label: tOverview("general"), children: null, span: 2 },
                {
                  label: (
                    <Descriptions.Label>
                      {t("general.owner", { count: 1 })}
                    </Descriptions.Label>
                  ),
                  children: (
                    <Users
                      countKey="general.owner"
                      list={record.codeOwners}
                      count={record.codeOwnersCount}
                      t={t}
                    />
                  )
                },
                {
                  label: (
                    <Descriptions.Label>
                      {t("general.contributors")}
                    </Descriptions.Label>
                  ),
                  children: (
                    <Users
                      list={record.contributors}
                      count={record.contributorsCount}
                      t={t}
                    />
                  )
                },
                {
                  label: (
                    <Descriptions.Label>
                      {tOverview("defaultBranch")}
                    </Descriptions.Label>
                  ),
                  children: record.branchName
                },
                {
                  span: -1,
                  label: (
                    <Descriptions.Label>
                      {tOverview("packageManagers")}
                    </Descriptions.Label>
                  ),
                  children: (
                    <PackageManagersList
                      packageManagers={record.packageManagers}
                    />
                  )
                },
                {
                  span: -1,
                  classNames: { content: styles.minWidth0 },
                  label: (
                    <Descriptions.Label>
                      {t("general.repositoryTopics")}
                    </Descriptions.Label>
                  ),
                  children: (
                    <RepositoryTopics
                      placement="right"
                      topics={record.topics}
                      t={t}
                      classes={{ container: styles.topics }}
                      empty={{ text: NO_VALUE_PLACEHOLDER }}
                    />
                  )
                },
                { label: <Descriptions.Divider />, children: null, span: 2 },
                {
                  label: (
                    <Descriptions.Label>
                      {tOverview("lastCommit")}
                    </Descriptions.Label>
                  ),
                  children: record.commitHash ? (
                    <Flex gap={4} align="flex-start">
                      <CommitHash
                        commitHash={record.commitHash}
                        committerName={record.committerName}
                        commitTimestamp={record.commitTimestamp}
                        href={record.commitUrl}
                        title={t("general.commit.lastChange")}
                        t={t}
                      />
                    </Flex>
                  ) : null
                },
                {
                  label: (
                    <Descriptions.Label>
                      {tOverview("lastCodeScan")}
                    </Descriptions.Label>
                  ),
                  children:
                    record.lastDetectionData?.lastScan || NO_VALUE_PLACEHOLDER
                },
                {
                  label: (
                    <Descriptions.Label>
                      {tOverview("status")}
                    </Descriptions.Label>
                  ),
                  children: <ScanStatusTag status={record.scanInfo.status} />
                },
                { label: <Descriptions.Divider />, children: null, span: 2 },
                {
                  label: (
                    <Heading
                      level={6}
                      title="Vulnerability Data Update"
                      popover={{
                        content:
                          "Continuously matches new vulnerabilities to this repository. Findings may change without code updates."
                      }}
                    />
                  ),
                  span: 2,
                  children: null
                },
                {
                  label: (
                    <Descriptions.Label>
                      {tOverview("lastSync")}
                    </Descriptions.Label>
                  ),
                  children: record.lastCveScan
                }
              ]}
            />
          </Col>
          <Col span={10}>
            <ResourceOverviewCard
              isLoading={isLoading || !repositoryName}
              type="ImageRepository"
              images={record.relatedImages || []}
              vms={record.relatedVms || []}
              imageSource="kodem"
              paramsExternalLink={record.queryParamsRelatedImagesExternalLink}
            />
          </Col>
        </Row>
      </Flex>
    )
  }
